import { jsPDF } from "jspdf";
import _ from "lodash";
import { CategoryData, CategoryType } from "src/revamp/types/PatientFormTypes";
import { User } from "src/services/dto/Security";

import {
  PersonalSleepHistoryOptions,
  epworthSleepinessScaleOptions,
  socialStatusesData,
  specificMedicalProblemsData,
} from "../../constants/PatientFormConstants";
import { Medication } from "../../types/MedicationTypes";
import { calculateBMI, formatPhoneNumber, getName } from "../helpers";
import { PlexFont, PoppinNormal } from "./constants";
import {
  PDFGeneratorState,
  drawFooter,
  drawHeader,
  drawRow,
  drawSectionTitle,
  drawTitle,
  drawValue,
  endSection,
  formatDate,
  getMedicationDosage,
  getMedicationName,
  getValue,
} from "./helper";

const drawPatientInfo = (state: PDFGeneratorState) => {
  drawRow(state, "Name:", getName(state.patient));
  drawRow(state, "Date of Birth:", state.patient.dateOfBirth);
  drawRow(state, "Gender:", state.patient.gender);
  drawRow(state, "Phone:", formatPhoneNumber(state.patient.phone));
  drawRow(state, "Email:", state.patient.email);
  endSection(state);
};

const drawObjective = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Objective:");
  const reviewOfSystems = getValue(state, "reviewOfSystems");
  const height = reviewOfSystems.height;
  const weight = reviewOfSystems.weight;
  const unit = reviewOfSystems.unit;

  const heightStr =
    unit === "Imperial" ? `${height.feet || 0}'${height.inches || 0}"` : `${height.cm || 0} cm`;

  const weightStr = unit === "Imperial" ? `${weight.pounds || 0} lbs` : `${weight.kg || 0} kg`;

  const bmi = calculateBMI({ height, weight, unit });

  drawValue(state, "Height:", heightStr);
  drawValue(state, "Weight:", weightStr);
  drawValue(state, "BMI:", bmi.toFixed(1));

  drawValue(state, "Recent Symptoms:");
  const recentSymptoms = getValue(state, "reviewOfSystems.recentSymptoms") || [];
  if (_.isEmpty(recentSymptoms)) {
    drawValue(state, "No recent symptoms reported");
  } else {
    recentSymptoms.forEach((symptom: string) => drawValue(state, `- ${symptom}`));
  }

  endSection(state);
};

const drawMedications = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Medications:");
  const medications = getValue(state, "medications.activeMedications") || [];

  if (_.isEmpty(medications)) {
    drawValue(state, "No medications reported");
  } else {
    drawValue(state, "The patient is currently taking the following medications:");
    medications.forEach((med: Medication) => {
      const name = getMedicationName(med);
      const dosage = getMedicationDosage(med);
      const frequency = med.frequency || "";
      drawValue(state, `- ${name}: ${dosage} ${frequency}`);
    });
  }

  endSection(state);
};

const drawAllergies = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Allergies:");
  const allergies = getValue(state, "medications.medicationAllergies") || [];

  if (_.isEmpty(allergies)) {
    drawValue(state, "No allergies reported");
  } else {
    drawValue(state, "The patient is allergic to the following medications:");
    allergies.forEach((allergy: any) => {
      drawValue(state, `- ${allergy.name}`);
    });
  }

  endSection(state);
};

const drawSocialHistory = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Social History:");
  const socialStatuses = getValue(state, "socialHistory.socialStatuses");

  if (socialStatuses) {
    socialStatusesData.forEach((item) => {
      const index = socialStatuses.findIndex((status: any) => status.name === item.value);
      if (index === -1) {
        drawValue(state, `- ${_.upperFirst(item.value)}: No`);
      } else {
        drawValue(
          state,
          `- ${_.upperFirst(item.value)}: Yes, ${socialStatuses[index].description}`,
        );
      }
    });
  }

  endSection(state);
};

const drawSleepMedicineHistory = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Sleep Medicine History:");
  const questionnaire = getValue(state, "sleepMedicineHistory.sleepQuestionnaire");

  if (questionnaire) {
    drawValue(
      state,
      "Patient's subjective sleep quality, compared to before last visit:",
      questionnaire.sleepStatus || "",
    );
    drawValue(
      state,
      "Average hours of sleep per night:",
      `${questionnaire.averageHoursSlept || 0} hours`,
    );
    drawValue(state, "Average Bedtime:", `${questionnaire.averageBedtime}`);
    drawValue(state, "Average Rise Time:", `${questionnaire.averageRiseTime}`);
    drawValue(
      state,
      "Average time it takes to fall asleep once in bed:",
      `${questionnaire.averageMinutesToDoze || 0} minutes`,
    );
    drawValue(state, "Number of Awakenings:", `${questionnaire.numberOfAwakenings || 0}`);
  }

  endSection(state);
};

const drawEpworthSleepinessScale = (state: PDFGeneratorState) => {
  drawSectionTitle(state, "Epworth Sleep Score:");
  const epworth = getValue(state, "sleepMedicineHistory.epworthSleepinessScale");

  if (epworth) {
    drawValue(state, "How likely is the patient to doze off in the situations below:");
    for (const option of epworthSleepinessScaleOptions) {
      const value = epworth[option.name];
      if (value) {
        drawValue(state, `- ${option.label}:`, value as string);
      }
    }
    drawValue(state, "SCORE:", epworth.score.toString());
  }

  endSection(state);
};

// Main generator function
export const generateFollowUpPDF = (
  patient: User,
  formData: Record<CategoryType, CategoryData>,
): jsPDF => {
  // Initialize state
  const state: PDFGeneratorState = {
    doc: new jsPDF("p", "mm", "letter"),
    y: 12,
    patient,
    formData,
  };

  // Setup fonts
  state.doc.addFileToVFS("Poppins-SemiBold.ttf", PlexFont);
  state.doc.addFont("Poppins-SemiBold.ttf", "PoppinsBold", "normal");
  state.doc.addFileToVFS("Poppins-Regular.ttf", PoppinNormal);
  state.doc.addFont("Poppins-Regular.ttf", "PoppinsNormal", "normal");

  // Draw content
  drawHeader(state);
  drawTitle(state, "Follow-Up Forms");

  // Only include information confirmed by Follow-Up form
  drawPatientInfo(state);
  drawObjective(state);
  drawMedications(state);
  drawAllergies(state);
  drawSocialHistory(state);
  drawSleepMedicineHistory(state);
  drawEpworthSleepinessScale(state);

  drawFooter(state);

  return state.doc;
};
